import { VTooltip, VTooltipContent, VTooltipTrigger } from '@vrzl/ui';
import { AlertTriangle, X, type LucideIcon } from 'lucide-react';

import type { CriterionGroupCounts } from '../utils/groupCriteria';

type StatusIndicatorProps = {
  icon: LucideIcon;
  /** Circle and icon colours; tokens only. */
  className: string;
  /** Short accessible name, e.g. "2 inconsistent". */
  label: string;
  title: string;
  description: string;
};

/**
 * Icon with tooltip for one status count in a group header. A real button, so
 * keyboard users can focus it and get the tooltip; it must therefore sit next
 * to the accordion trigger, not inside it (no button in a button).
 */
function StatusIndicator({ icon: Icon, className, label, title, description }: StatusIndicatorProps) {
  return (
    <VTooltip>
      <VTooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={`inline-flex size-7 shrink-0 cursor-help items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
        >
          <Icon size={16} aria-hidden="true" />
        </button>
      </VTooltipTrigger>
      <VTooltipContent>
        <div className="font-medium">{title}</div>
        <div className="mt-1 text-muted-foreground">{description}</div>
      </VTooltipContent>
    </VTooltip>
  );
}

/**
 * The "inconsistent" and "niet aanwezig" indicators of a group header, shared
 * by the Information and Validation screens so both look and read the same.
 */
export function GroupStatusIndicators({ counts }: { counts: CriterionGroupCounts }) {
  return (
    <>
      {counts.inconsistent > 0 && (
        <StatusIndicator
          icon={AlertTriangle}
          className="bg-warning-100 text-warning-600"
          label={`${counts.inconsistent} inconsistent`}
          title="Inconsistent"
          description={`${counts.inconsistent} ${
            counts.inconsistent === 1 ? 'criterium geeft' : 'criteria geven'
          } tegenstrijdige waarden in de documenten. Controleer de bronnen.`}
        />
      )}
      {counts.missing > 0 && (
        <StatusIndicator
          icon={X}
          className="bg-error-100 text-error-600"
          label={`${counts.missing} niet aanwezig`}
          title="Niet aanwezig"
          description={`${counts.missing} ${
            counts.missing === 1 ? 'criterium is' : 'criteria zijn'
          } niet aanwezig in de documenten.`}
        />
      )}
    </>
  );
}
